import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { useState } from 'react';
import { 
    Shield, 
    ChevronRight,
    FileText,
    DollarSign,
    Settings as SettingsIcon,
    HelpCircle,
    Download,
    Star,
    Loader2,
    Users,
    Image,
    Receipt
} from 'lucide-react';

export const Settings = () => {
    const { user, isAdmin } = useAuth();
    const [downloadingBackup, setDownloadingBackup] = useState(false);

    const handleDownloadBackup = async () => {
        setDownloadingBackup(true);
        try {
            const res = await adminAPI.downloadBackup();
            const blob = new Blob([res.data], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `plaster-survey-backup-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Backup downloaded!');
        } catch {
            toast.error('Failed to download backup');
        } finally {
            setDownloadingBackup(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-4">
                <div>
                    <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight text-slate-900">
                        Settings
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">Manage your account</p>
                </div>

                {/* Profile Card */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                                <span className="text-2xl font-bold text-white">
                                    {user?.name?.charAt(0) || 'U'}
                                </span>
                            </div>
                            <div>
                                <p className="font-semibold text-lg text-slate-900">{user?.name}</p>
                                <p className="text-slate-500">{user?.email}</p>
                                <div className="flex items-center gap-1 mt-1">
                                    <Shield className="h-4 w-4 text-slate-400" />
                                    <span className="text-sm text-slate-500 capitalize">{user?.role}</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Admin Links */}
                {isAdmin() && (
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight">
                                Admin Tools
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {/* Branding */}
                            <Link
                                to="/admin/branding"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-branding-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                        <Image className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Branding</p>
                                        <p className="text-sm text-slate-500">Logo & application name</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            {/* Users — top of admin tools */}
                            <Link
                                to="/admin/users"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-users-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                        <Users className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Users</p>
                                        <p className="text-sm text-slate-500">Manage accounts & reset passwords</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/materials"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-materials-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                        <Download className="h-5 w-5 text-teal-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Materials Price List</p>
                                        <p className="text-sm text-slate-500">Materials, compositions & order estimates</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/extras"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-extras-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                        <SettingsIcon className="h-5 w-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Job Extras</p>
                                        <p className="text-sm text-slate-500">Waste removal, skip hire & site extras</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/rates"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-rates-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <DollarSign className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Rate Card</p>
                                        <p className="text-sm text-slate-500">Manage pricing rates</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/presets"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-presets-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <SettingsIcon className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Adjustment Presets</p>
                                        <p className="text-sm text-slate-500">Manage quick adjustments</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/template"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-template-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                        <FileText className="h-5 w-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Quote Template</p>
                                        <p className="text-sm text-slate-500">Branding & T&Cs</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/invoice-template"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-invoice-template-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                        <Receipt className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Invoice Template</p>
                                        <p className="text-sm text-slate-500">Payment terms & bank details</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/export"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-export-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <FileText className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Data Export</p>
                                        <p className="text-sm text-slate-500">Export customers & jobs</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <Link
                                to="/admin/template"
                                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
                                data-testid="admin-review-link"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                        <Star className="h-5 w-5 text-yellow-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">Google Review Template</p>
                                        <p className="text-sm text-slate-500">Configure review request message</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            </Link>
                            <div className="p-4">
                                <Button
                                    onClick={handleDownloadBackup}
                                    disabled={downloadingBackup}
                                    variant="outline"
                                    className="w-full h-11 border-slate-200 text-slate-700"
                                    data-testid="download-backup-btn"
                                >
                                    {downloadingBackup ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
                                    ) : (
                                        <><Download className="h-4 w-4 mr-2" /> Download Backup</>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Help */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                <HelpCircle className="h-5 w-5 text-slate-500" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Help & Support</p>
                                <p className="text-sm text-slate-500">Plaster Survey & Quote v1.0</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};
